import { ref, readonly, computed, watch, triggerRef } from 'vue';
import { decode as decodeMsgpack } from '@msgpack/msgpack';
import { computeIsDaytime } from './useSolarPosition';
import { getSatMetadata, SAT_NORAD_MAP } from './useAoiStore';
import { parseUtcEpochMs } from './useDateTimeSettings';
import { useHexTimingPerf } from './useHexTimingPerf';
import { isOroraTechConstellationSatellite } from './useConstellationGrouping';
import { authFetch } from './useAuthFetch';
import { useAuth } from './useAuth';
import { normalizePassDirection, type PassDirection } from './passDirection';
import { isColorblindFriendly } from './useAccessibilitySettings';
import type {
  TimingDecodeRequest,
  TimingDecodeResponse,
  WorkerHexTimingShardPayload,
} from '@/workers/hexTimingWorkerTypes';

/* ──────────────────────────── Types ──────────────────────────── */

export type MapViewMode = 'grid' | 'swath' | 'aoi';
export type OtDataSourceFilter = 'predicted' | 'scheduled';

export interface HexPassShardIndex {
  version: number;
  prefixLength: number;
  totalCells: number;
  totalShards: number;
  shards: Record<string, string>;
  format?: 'json' | 'msgpack';
  extendedShards?: HexTemporalShardRef[];
}

export interface HexTemporalShardRef {
  indexUrl: string;
  startTime: string;
  endTime: string;
  satelliteGroup: 'ot';
}

/** Lightweight timing data: { h3_index: { satellite: [epoch_seconds, ...] } } */
export type HexTimingCellData = Record<string, number[]>;
export type HexTimingShardPayload = Record<string, HexTimingCellData>;

export interface HexPassEntry {
  satellite: string;
  start_time: string;
  end_time?: string;
  data_source?: string;
  cloud_cover_pct?: number | null;
  /** Compact API-only direction from the hex pass shard. */
  d?: 'A' | 'D';
}

export type HexPassShardPayload = Record<string, HexPassEntry[]>;

export interface SatelliteMetadata {
  satellites: string[];
  constellations: string[];
  operators: string[];
  sensor_types: string[];
  spatial_resolution_ranges: string[];
  minTime: string;
  maxTime: string;
  lastUpdated: string;
  defaultForecastHours?: number;
  /** How far back the prediction service can answer, in hours. */
  historyHours?: number;
  /** Full live prediction window advertised by tle-predictor. */
  servedWindowStart?: string;
  servedWindowEnd?: string;
}

/**
 * The prediction service's own view of the fleet, from GET /api/satellites.
 *
 * This is the authority for which satellites exist, for their Swath Extents — the
 * service calibrates those against real granule footprints, and they can be
 * asymmetric, as with Sentinel-3's 1000 km starboard / 500 km port — and for the
 * window it can currently answer over. The static catalogue in useAoiStore remains
 * the authority for presentation only (sensor type, resolution, constellation),
 * which the service does not carry.
 */
export interface SatelliteRosterEntry {
  noradId: number;
  name: string;
  swathStarboardKm: number;
  swathPortKm: number;
  elementsEpoch?: string;
}

export interface SatelliteRoster {
  byNorad: Map<number, SatelliteRosterEntry>;
  byName: Map<string, number>;
  noradIds: number[];
  names: string[];
  servedWindowStart?: string;
  servedWindowEnd?: string;
}

interface RawRosterSatellite {
  norad_id?: number | string;
  name?: string;
  swath_starboard_km?: number | string;
  swath_port_km?: number | string;
  elements_epoch?: string;
}

interface RawRosterDocument {
  satellites?: RawRosterSatellite[];
  served_window?: { start?: string; end?: string };
  /** Display names resolved by the backend, so both ends agree on naming. */
  display_names?: Record<string, string>;
}

function parseRoster(document: RawRosterDocument): SatelliteRoster | null {
  const entries = Array.isArray(document.satellites) ? document.satellites : [];
  const byNorad = new Map<number, SatelliteRosterEntry>();
  const byName = new Map<string, number>();

  for (const raw of entries) {
    const noradId = Number(raw?.norad_id);
    if (!Number.isFinite(noradId)) continue;
    const name = String(
      document.display_names?.[String(noradId)] ?? raw?.name ?? `NORAD-${noradId}`,
    ).trim();
    if (!name) continue;
    byNorad.set(noradId, {
      noradId,
      name,
      swathStarboardKm: Number(raw?.swath_starboard_km) || 0,
      swathPortKm: Number(raw?.swath_port_km) || 0,
      elementsEpoch: raw?.elements_epoch,
    });
    byName.set(name.toUpperCase(), noradId);
  }

  if (byNorad.size === 0) return null;
  return {
    byNorad,
    byName,
    noradIds: [...byNorad.keys()],
    names: [...byNorad.values()].map((entry) => entry.name),
    servedWindowStart: document.served_window?.start,
    servedWindowEnd: document.served_window?.end,
  };
}

export interface VisiblePass {
  /** Unique aggregated-overpass key */
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  data_source?: string;
  direction?: PassDirection;
  sensor_type?: string;
  spatial_res_m?: number;
  constellation?: string;
  tasking?: boolean;
  is_daytime?: boolean;
  cloud_cover_pct?: number | null;
  /** WGS-84 centroid latitude of the overpass footprint (for latency lookup) */
  centroid_lat?: number;
  /** Fraction of the AOI this pass actually observed (0-1); AOI passes only. */
  covered_fraction?: number | null;
  // Possible future addition: elements_epoch — the API returns it on every feature
  // and it says how old the orbital data behind a prediction is (up to 15 days).
}

/* ──────────────────────── Shared State ─────────────────────── */

const metadata = ref<SatelliteMetadata | null>(null);
const satelliteRoster = ref<SatelliteRoster | null>(null);
const loading = ref(true);
const swathLoading = ref(false);

function setSwathLoading(value: boolean): void {
  swathLoading.value = value;
}

const FALLBACK_FORECAST_HOURS = 48;
const HOUR_MS = 60 * 60 * 1000;
const INITIAL_NOW = Date.now();
const metaTimeRange = ref<[number, number]>([INITIAL_NOW, INITIAL_NOW + FALLBACK_FORECAST_HOURS * HOUR_MS]);
const standardForecastEndMs = ref(INITIAL_NOW + FALLBACK_FORECAST_HOURS * HOUR_MS);

// User-adjustable time range (epoch-ms) — driven by graph drag handles or list date pickers
const timeRange = ref<[number, number]>([INITIAL_NOW, INITIAL_NOW + FALLBACK_FORECAST_HOURS * HOUR_MS]);

// Satellite name selections — driven by MapFilterPanel checkboxes
// Default is resolved from metadata at runtime (all satellites).
const DEFAULT_SATELLITES: string[] = [];
const SATELLITE_STORAGE_KEY = 'overpassPlanner.selectedSatellites';
const ORORA_CONSTELLATION_EXPANDED_STORAGE_KEY = 'overpassPlanner.ororaConstellationExpanded';

function loadPersistedSatellites(): string[] {
  if (typeof window === 'undefined') return [...DEFAULT_SATELLITES];
  try {
    const raw = window.localStorage.getItem(SATELLITE_STORAGE_KEY);
    if (!raw) return [...DEFAULT_SATELLITES];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_SATELLITES];
    const cleaned = parsed
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());
    return cleaned.length ? cleaned : [...DEFAULT_SATELLITES];
  } catch {
    return [...DEFAULT_SATELLITES];
  }
}

const selectedSatelliteNames = ref<string[]>(loadPersistedSatellites());

function loadPersistedOroraConstellationExpanded(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(ORORA_CONSTELLATION_EXPANDED_STORAGE_KEY);
    return raw === 'true';
  } catch {
    return false;
  }
}

const ororaConstellationExpanded = ref<boolean>(loadPersistedOroraConstellationExpanded());

watch(
  selectedSatelliteNames,
  (names) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SATELLITE_STORAGE_KEY, JSON.stringify(names));
    } catch {
      // Ignore persistence failures (private mode / storage limits)
    }
  },
  { deep: true },
);

watch(ororaConstellationExpanded, (expanded) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ORORA_CONSTELLATION_EXPANDED_STORAGE_KEY, String(expanded));
  } catch {
    // Ignore persistence failures (private mode / storage limits)
  }
});

// Cloud cover threshold: max cloud cover % to show (0–100, 100 = show all)
const CLOUD_COVER_THRESHOLD_STORAGE_KEY = 'overpassPlanner.cloudCoverThreshold';

function loadPersistedCloudCoverThreshold(): number {
  if (typeof window === 'undefined') return 100;
  try {
    const raw = window.localStorage.getItem(CLOUD_COVER_THRESHOLD_STORAGE_KEY);
    if (raw == null) return 100;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return 100;
    return Math.max(0, Math.min(100, Math.round(parsed)));
  } catch {
    return 100;
  }
}

const cloudCoverThreshold = ref<number>(loadPersistedCloudCoverThreshold());

watch(cloudCoverThreshold, (value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CLOUD_COVER_THRESHOLD_STORAGE_KEY, String(value));
  } catch { /* ignore */ }
});

// OroraTech data source filter: 'predicted' (api) vs 'scheduled' (forest_client)
const OT_DATA_SOURCE_STORAGE_KEY = 'overpassPlanner.otDataSourceFilter';

function loadPersistedOtDataSource(): OtDataSourceFilter {
  if (typeof window === 'undefined') return 'predicted';
  try {
    const raw = window.localStorage.getItem(OT_DATA_SOURCE_STORAGE_KEY);
    if (raw === 'predicted' || raw === 'scheduled') return raw;
  } catch { /* ignore */ }
  return 'predicted';
}

const otDataSourceFilter = ref<OtDataSourceFilter>(loadPersistedOtDataSource());

watch(otDataSourceFilter, (value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(OT_DATA_SOURCE_STORAGE_KEY, value);
  } catch { /* ignore */ }
});

function normalizeSatelliteKey(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]+/g, '');
}

function reconcileSelectedSatellites(availableSatellites: string[]): string[] {
  if (availableSatellites.length === 0) return [];

  const availableByUpper = new Map<string, string>();
  const availableByNormalized = new Map<string, string>();
  for (const sat of availableSatellites) {
    const name = String(sat ?? '').trim();
    if (!name) continue;
    availableByUpper.set(name.toUpperCase(), name);
    const normalized = normalizeSatelliteKey(name);
    if (normalized && !availableByNormalized.has(normalized)) {
      availableByNormalized.set(normalized, name);
    }
  }

  const reconciled: string[] = [];
  const seen = new Set<string>();
  for (const persisted of selectedSatelliteNames.value) {
    const raw = String(persisted ?? '').trim();
    if (!raw) continue;

    const exact = availableByUpper.get(raw.toUpperCase());
    const fallback = availableByNormalized.get(normalizeSatelliteKey(raw));
    const resolved = exact ?? fallback;
    if (!resolved) continue;

    const dedupeKey = resolved.toUpperCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    reconciled.push(resolved);
  }

  const missingSatellites = availableSatellites.filter(
    (sat) => !seen.has(sat.toUpperCase()),
  );
  if (
    missingSatellites.length === 1
    && missingSatellites[0].toUpperCase() === 'FENGYUN 3F'
    && reconciled.length === availableSatellites.length - 1
  ) {
    return [...availableSatellites];
  }

  if (reconciled.length > 0) return reconciled;
  return [...availableSatellites];
}

function sameSelection(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].toUpperCase() !== b[i].toUpperCase()) return false;
  }
  return true;
}

function getForecastWindows(data: SatelliteMetadata, roster: SatelliteRoster | null): {
  available: [number, number];
  initial: [number, number];
  standardEnd: number;
} {
  const now = Date.now();
  const minMs = parseUtcEpochMs(data.minTime);
  const maxMs = parseUtcEpochMs(data.maxTime);
  const defaultHours = Number.isFinite(data.defaultForecastHours)
    ? Math.max(1, Number(data.defaultForecastHours))
    : FALLBACK_FORECAST_HOURS;
  // The live roster wins over the static metadata copy: the metadata file is
  // rewritten once a day by the pipeline, so its window is stale by up to that
  // long, while the service's own window slides continuously.
  const servedStartMs = parseUtcEpochMs(roster?.servedWindowStart ?? data.servedWindowStart ?? '');
  const servedEndMs = parseUtcEpochMs(roster?.servedWindowEnd ?? data.servedWindowEnd ?? '');
  // The service answers backwards as well as forwards. Nothing is precomputed for
  // the past in the static hex artifacts, but the swath and AOI views are live
  // queries. Use the predictor's advertised served window for the shared range;
  // older metadata falls back to the generated artifact bounds.
  const historyHours = Number.isFinite(data.historyHours)
    ? Math.max(0, Number(data.historyHours))
    : 0;

  const historyStart = now - historyHours * HOUR_MS;
  const fallbackStart = historyHours > 0
    ? Math.min(Number.isFinite(minMs) ? minMs : now, historyStart)
    : (Number.isFinite(minMs) ? minMs : now);
  const fallbackEnd = Number.isFinite(maxMs)
    ? maxMs
    : now + defaultHours * HOUR_MS;
  const availableStart = Number.isFinite(servedStartMs) ? servedStartMs : fallbackStart;
  const availableEnd = Number.isFinite(servedEndMs) ? servedEndMs : fallbackEnd;
  if (!Number.isFinite(availableStart) || !Number.isFinite(availableEnd) || availableEnd <= availableStart) {
    const standardEnd = now + defaultHours * HOUR_MS;
    return { available: [now, standardEnd], initial: [now, standardEnd], standardEnd };
  }

  const start = now >= availableStart && now <= availableEnd ? now : availableStart;
  const standardEnd = Math.min(availableEnd, start + defaultHours * HOUR_MS);
  return {
    available: [availableStart, availableEnd],
    initial: [start, standardEnd],
    standardEnd,
  };
}

// Currently selected overpass (highlighted on map, shown in tooltip)
const selectedPassId = ref<string | null>(null);
const hoveredPassId = ref<string | null>(null);
const selectedPassSourceIds = ref<string[]>([]);
const hoveredPassSourceIds = ref<string[]>([]);

function normalizePassSourceIds(passId: string | null, sourceIds?: string[]): string[] {
  const cleaned = (sourceIds ?? [])
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  if (cleaned.length > 0) return [...new Set(cleaned)];
  if (passId) return [passId];
  return [];
}

function setSelectedPass(passId: string | null): void {
  selectedPassId.value = passId;
  selectedPassSourceIds.value = passId ? [passId] : [];
}

/** When true, hovered pass highlights use coverage-graph teal instead of timing colors */
const coverageHoverActive = ref(false);

function setHoveredPass(passId: string | null, sourceIds?: string[], coverageMode = false): void {
  hoveredPassId.value = passId;
  hoveredPassSourceIds.value = normalizePassSourceIds(passId, sourceIds);
  coverageHoverActive.value = coverageMode && passId !== null;
}

watch(selectedPassId, (id) => {
  if (!id) {
    if (selectedPassSourceIds.value.length) selectedPassSourceIds.value = [];
    return;
  }
  if (!selectedPassSourceIds.value.includes(id)) {
    selectedPassSourceIds.value = [id];
  }
});

watch(hoveredPassId, (id) => {
  if (!id) {
    if (hoveredPassSourceIds.value.length) hoveredPassSourceIds.value = [];
    return;
  }
  if (!hoveredPassSourceIds.value.includes(id)) {
    hoveredPassSourceIds.value = [id];
  }
});

// Layer visibility & opacity (driven by Layers panel)
const DEFAULT_FEATURE_LAYER_OPACITY = 0.45;
const DEFAULT_HEX_GRID_OPACITY = 0.75;
export const COLORBLIND_FEATURE_MIN_OPACITY = 0.7;
export const COLORBLIND_HEX_MIN_OPACITY = 0.75;

const overpassLayerVisible = ref(true);
const overpassLayerOpacity = ref(
  isColorblindFriendly.value ? COLORBLIND_FEATURE_MIN_OPACITY : DEFAULT_FEATURE_LAYER_OPACITY,
);

// EUMETSAT cloud layer visibility & opacity
const eumetsatTrueRgbVisible = ref(false);
const eumetsatTrueRgbOpacity = ref(0.85);
const eumetsatInfraredVisible = ref(false);
const eumetsatInfraredOpacity = ref(0.85);

// Hex grid layer visibility & opacity
const hexGridVisible = ref(true);
const hexGridOpacity = ref(
  isColorblindFriendly.value ? COLORBLIND_HEX_MIN_OPACITY : DEFAULT_HEX_GRID_OPACITY,
);

// AOI coverage layer visibility & opacity
const aoiCoverageVisible = ref(true);
const aoiCoverageOpacity = ref(
  isColorblindFriendly.value ? COLORBLIND_FEATURE_MIN_OPACITY : DEFAULT_FEATURE_LAYER_OPACITY,
);

interface FeatureLayerOpacitySnapshot {
  overpass: number;
  hexGrid: number;
  aoiCoverage: number;
}

let opacityBeforeColorblindMode: FeatureLayerOpacitySnapshot | null = isColorblindFriendly.value
  ? {
      overpass: DEFAULT_FEATURE_LAYER_OPACITY,
      hexGrid: DEFAULT_HEX_GRID_OPACITY,
      aoiCoverage: DEFAULT_FEATURE_LAYER_OPACITY,
    }
  : null;

watch(isColorblindFriendly, (enabled) => {
  if (enabled) {
    opacityBeforeColorblindMode = {
      overpass: overpassLayerOpacity.value,
      hexGrid: hexGridOpacity.value,
      aoiCoverage: aoiCoverageOpacity.value,
    };
    overpassLayerOpacity.value = Math.max(
      overpassLayerOpacity.value,
      COLORBLIND_FEATURE_MIN_OPACITY,
    );
    hexGridOpacity.value = Math.max(hexGridOpacity.value, COLORBLIND_HEX_MIN_OPACITY);
    aoiCoverageOpacity.value = Math.max(
      aoiCoverageOpacity.value,
      COLORBLIND_FEATURE_MIN_OPACITY,
    );
    return;
  }

  if (opacityBeforeColorblindMode) {
    overpassLayerOpacity.value = opacityBeforeColorblindMode.overpass;
    hexGridOpacity.value = opacityBeforeColorblindMode.hexGrid;
    aoiCoverageOpacity.value = opacityBeforeColorblindMode.aoiCoverage;
    opacityBeforeColorblindMode = null;
  }
});

// Visible passes (populated by MapSatelliteLayer)
const visiblePasses = ref<VisiblePass[]>([]);
const visiblePassCount = ref<number | null>(null);
const swathGraphPasses = ref<VisiblePass[]>([]);

// ── Grid (hex) view state ──

const MAP_VIEW_MODE_STORAGE_KEY = 'overpassPlanner.mapViewMode';
const MAP_VIEW_MODE_VALUES: MapViewMode[] = ['grid', 'swath', 'aoi'];

function loadPersistedMapViewMode(): MapViewMode {
  if (typeof window === 'undefined') return 'aoi';
  try {
    const raw = window.localStorage.getItem(MAP_VIEW_MODE_STORAGE_KEY);
    if (raw && (MAP_VIEW_MODE_VALUES as string[]).includes(raw)) return raw as MapViewMode;
  } catch { /* ignore */ }
  return 'aoi';
}

/** View mode: 'swath' shows full overpass polygons, 'grid' shows H3 hex grid, 'aoi' shows AOI coverage */
const mapViewMode = ref<MapViewMode>(loadPersistedMapViewMode());

watch(mapViewMode, (mode) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(MAP_VIEW_MODE_STORAGE_KEY, mode); } catch { /* ignore */ }
});

/** Selected H3 cell indices (multi-select) */
const selectedHexCells = ref<Set<string>>(new Set());

/** Cell centroid cache: h3_index → [lat, lon] (EPSG:4326). Populated by MapHexGridLayer. */
const hexCellCentroids = new Map<string, [number, number]>();

const RETRYABLE_STATUS = new Set([502, 503, 504]);

function _isAbortError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { name?: string; message?: string };
  return anyErr.name === 'AbortError' || (anyErr.message ?? '').toLowerCase().includes('aborted');
}

function _sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function _fetchJsonWithRetry<T>(
  url: string,
  options?: {
    attempts?: number;
    timeoutMs?: number;
    initialBackoffMs?: number;
  },
): Promise<T> {
  const attempts = Math.max(1, options?.attempts ?? 3);
  const timeoutMs = Math.max(1000, options?.timeoutMs ?? 8000);
  const initialBackoffMs = Math.max(100, options?.initialBackoffMs ?? 300);
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await authFetch(url, { signal: controller.signal });
      if (!res.ok) {
        const err = new Error(`Fetch failed (${res.status}) for ${url}`);
        if (!RETRYABLE_STATUS.has(res.status) || attempt === attempts) {
          throw err;
        }
        lastError = err;
      } else {
        const rawBuf = await res.arrayBuffer();
        const buf = await _maybeDecompressGzip(rawBuf);
        const text = new TextDecoder('utf-8').decode(buf);
        const data = JSON.parse(text) as T;
        return data;
      }
    } catch (err) {
      lastError = err;
      if (attempt === attempts) break;
    } finally {
      clearTimeout(timeoutHandle);
    }

    const backoff = initialBackoffMs * (2 ** (attempt - 1));
    const jitter = Math.floor(Math.random() * 200);
    await _sleep(backoff + jitter);
  }

  throw lastError ?? new Error(`Fetch failed for ${url}`);
}

/* ─────────── Timing shards (fast viewport coloring + pass list) ─────────── */

/** Per-cell timing data: { satellite_name: [epoch_seconds, ...] } */
const hexTimingData = ref<Record<string, HexTimingCellData> | null>(null);
const hexTimingDataRevision = ref(0);
const hexTimingIndex = ref<HexPassShardIndex | null>(null);
const hexTimingLastMergeAt = ref(0);
let _hexTimingIndexLoaded = false;
const _loadedTimingShardKeys = new Set<string>();
const _timingShardInFlight = new Map<string, Promise<boolean>>();
const _failedTimingShardAt = new Map<string, number>();
const _extendedTimingIndexes = new Map<string, HexPassShardIndex>();
const _extendedTimingIndexInFlight = new Map<string, Promise<HexPassShardIndex | null>>();
const MAX_TIMING_SHARD_CONCURRENCY = 8;
const MAX_TIMING_PREFETCH_CONCURRENCY = 2;
const MAX_TIMING_PREFETCH_KEYS = 4;
const TIMING_SHARD_RETRY_COOLDOWN_MS = 10000;
const HEX_GRID_PIPELINE_V2 = import.meta.env.VITE_HEX_GRID_PIPELINE_V2 !== '0';
const hexTimingPerf = useHexTimingPerf();
const { recordSample } = hexTimingPerf;

async function loadHexTimingIndex() {
  if (_hexTimingIndexLoaded) return;
  _hexTimingIndexLoaded = true;
  try {
    hexTimingIndex.value = await _fetchJsonWithRetry<HexPassShardIndex>(
      '/data/satellite_hex_timing_index.json',
      { attempts: 3, timeoutMs: 7000, initialBackoffMs: 250 },
    );
    if (!hexTimingData.value) {
      hexTimingData.value = {};
    }
  } catch (err) {
    _hexTimingIndexLoaded = false;
    console.warn('Failed to load hex timing index (will retry on next trigger):', err);
  }
}

interface HexShardSource {
  id: string;
  index: HexPassShardIndex;
}

const BASE_HEX_SHARD_SOURCE_ID = 'base';

function _isTemporalShardActive(shard: HexTemporalShardRef): boolean {
  const shardStart = parseUtcEpochMs(shard.startTime);
  const shardEnd = parseUtcEpochMs(shard.endTime);
  if (!Number.isFinite(shardStart) || !Number.isFinite(shardEnd)) return false;
  return shardStart < timeRange.value[1] && shardEnd > timeRange.value[0];
}

function _sourceShardKey(sourceId: string, shardKey: string): string {
  return `${sourceId}::${shardKey}`;
}

async function _loadExtendedHexIndex(
  shard: HexTemporalShardRef,
  cache: Map<string, HexPassShardIndex>,
  inFlight: Map<string, Promise<HexPassShardIndex | null>>,
  label: string,
): Promise<HexPassShardIndex | null> {
  const cached = cache.get(shard.indexUrl);
  if (cached) return cached;
  const existing = inFlight.get(shard.indexUrl);
  if (existing) return existing;

  const task = _fetchJsonWithRetry<HexPassShardIndex>(
    shard.indexUrl,
    { attempts: 2, timeoutMs: 7000, initialBackoffMs: 250 },
  )
    .then((index) => {
      cache.set(shard.indexUrl, index);
      return index;
    })
    .catch((err) => {
      console.warn(`Failed to load extended ${label} index '${shard.indexUrl}':`, err);
      return null;
    })
    .finally(() => inFlight.delete(shard.indexUrl));
  inFlight.set(shard.indexUrl, task);
  return task;
}

async function _getActiveHexShardSources(
  baseIndex: HexPassShardIndex | null,
  cache: Map<string, HexPassShardIndex>,
  inFlight: Map<string, Promise<HexPassShardIndex | null>>,
  label: string,
): Promise<HexShardSource[]> {
  if (!baseIndex) return [];
  const sources: HexShardSource[] = [{ id: BASE_HEX_SHARD_SOURCE_ID, index: baseIndex }];
  const activeRefs = (baseIndex.extendedShards ?? []).filter(_isTemporalShardActive);
  const extended = await Promise.all(
    activeRefs.map(async (ref) => ({
      id: ref.indexUrl,
      index: await _loadExtendedHexIndex(ref, cache, inFlight, label),
    })),
  );
  for (const source of extended) {
    if (source.index) sources.push({ id: source.id, index: source.index });
  }
  return sources;
}

function _hasCellDataForActiveSources(
  cellId: string,
  baseIndex: HexPassShardIndex | null,
  extendedIndexes: Map<string, HexPassShardIndex>,
  loadedKeys: Set<string>,
): boolean {
  if (!baseIndex) return false;
  const sources: HexShardSource[] = [{ id: BASE_HEX_SHARD_SOURCE_ID, index: baseIndex }];
  for (const ref of baseIndex.extendedShards ?? []) {
    if (!_isTemporalShardActive(ref)) continue;
    const index = extendedIndexes.get(ref.indexUrl);
    if (!index) return false;
    sources.push({ id: ref.indexUrl, index });
  }

  for (const source of sources) {
    const prefixLength = Math.max(1, source.index.prefixLength ?? 3);
    const shardKey = cellId.slice(0, prefixLength);
    if (!source.index.shards[shardKey]) continue;
    if (!loadedKeys.has(_sourceShardKey(source.id, shardKey))) return false;
  }
  return true;
}

function hasTimingDataForCell(cellId: string): boolean {
  return _hasCellDataForActiveSources(
    cellId,
    hexTimingIndex.value,
    _extendedTimingIndexes,
    _loadedTimingShardKeys,
  );
}

function matchesSelectedSatellite(name: string): boolean {
  const satUpper = (name ?? '').toUpperCase();
  return selectedSatelliteNames.value.some((n) => n.toUpperCase() === satUpper);
}

function passWithinForecastHorizon(satellite: string, startMs: number, dataSource?: string): boolean {
  if (!Number.isFinite(startMs)) return false;
  // Live predictor responses are valid across the full served window. Static
  // hex artifacts still use the default horizon, with OT extensions handled by
  // their existing shard path.
  if (dataSource === 'api') return true;
  const isExtendedOtPath = isOroraTechConstellationSatellite(satellite) && dataSource !== 'forest_client';
  if (isExtendedOtPath) return true;
  return startMs <= standardForecastEndMs.value;
}

function normalizeCloudCoverPct(value: unknown): number | null {
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

/** Returns true when a pass's cloud cover is within the user-selected threshold */
function passWithinCloudThreshold(pass: VisiblePass): boolean {
  if (cloudCoverThreshold.value >= 100) return true;
  if (pass.cloud_cover_pct == null) return true; // no data → show
  return pass.cloud_cover_pct <= cloudCoverThreshold.value;
}

function buildGridPassId(satellite: string, startIso: string, dataSource?: string): string {
  const source = String(dataSource ?? '').trim();
  const startMs = parseUtcEpochMs(startIso);
  const normalizedStartIso = Number.isFinite(startMs) ? new Date(startMs).toISOString() : startIso;
  return `grid|${satellite}|${source}|${normalizedStartIso}`;
}

function matchesGridPassFilters(satName: string, dataSource?: string): boolean {
  if (!matchesSelectedSatellite(satName)) return false;
  if (isOroraTechConstellationSatellite(satName)) {
    const source = String(dataSource ?? '');
    if (otDataSourceFilter.value === 'predicted') {
      if (source === 'forest_client') return false;
    } else {
      if (source !== 'forest_client') return false;
    }
  }
  return true;
}

function buildGridVisiblePass(options: {
  satellite: string;
  startIso: string;
  endIso?: string;
  dataSource?: string;
  direction?: PassDirection;
  cloudCoverPct?: unknown;
  cellCenter?: [number, number];
}): VisiblePass | null {
  const startIsoRaw = String(options.startIso ?? '').trim();
  const satellite = String(options.satellite ?? '').trim();
  if (!satellite || !startIsoRaw) return null;

  const startMs = parseUtcEpochMs(startIsoRaw);
  if (!passWithinForecastHorizon(satellite, startMs, options.dataSource)) return null;
  const startIso = Number.isFinite(startMs) ? new Date(startMs).toISOString() : startIsoRaw;
  const fallbackEndIso = Number.isFinite(startMs)
    ? new Date(startMs + PASS_DURATION_MS).toISOString()
    : startIsoRaw;
  const endIsoRaw = String(options.endIso ?? '').trim() || fallbackEndIso;
  const parsedEndMs = parseUtcEpochMs(endIsoRaw);
  const endIso = Number.isFinite(parsedEndMs) ? new Date(parsedEndMs).toISOString() : endIsoRaw;
  const endMs = parseUtcEpochMs(endIso);
  const meta = getSatMetadata(satellite);
  const cloudCoverPct = options.dataSource === 'api' || startMs <= standardForecastEndMs.value
    ? normalizeCloudCoverPct(options.cloudCoverPct)
    : null;

  return {
    id: buildGridPassId(satellite, startIso, options.dataSource),
    name: satellite,
    start_time: startIso,
    end_time: endIso,
    data_source: options.dataSource,
    direction: options.direction,
    sensor_type: meta?.sensor_type,
    spatial_res_m: meta?.spatial_res_m,
    constellation: meta?.constellation,
    is_daytime: options.cellCenter && Number.isFinite(startMs) && Number.isFinite(endMs)
      ? computeIsDaytime(startMs, endMs, options.cellCenter[0], options.cellCenter[1])
      : undefined,
    cloud_cover_pct: cloudCoverPct,
    centroid_lat: options.cellCenter?.[0],
  };
}

/** Expand delta-encoded epoch arrays in-place.
 * v2: [first, d1, d2, ...] → [first, first+d1, first+d1+d2, ...]
 * v3: [t0, c0, dt1, c1, dt2, c2, ...] → timestamps at even indices delta-decoded, cloud values untouched
 */
function _deltaDecodePayload(payload: HexTimingShardPayload, version = 2): void {
  for (const cellId in payload) {
    const cellData = payload[cellId];
    for (const sat in cellData) {
      const arr = cellData[sat];
      if (version >= 3) {
        if (arr.length <= 2) continue;
        for (let i = 2; i < arr.length; i += 2) {
          arr[i] += arr[i - 2];
        }
      } else {
        if (arr.length > 1) {
          for (let i = 1; i < arr.length; i++) {
            arr[i] += arr[i - 1];
          }
        }
      }
    }
  }
}

/** Decompress gzip ArrayBuffer if the server didn't handle Content-Encoding.
 *
 * Uses Blob.stream().pipeThrough(DecompressionStream) → Response.arrayBuffer()
 * as the primary path, with pako as a fallback. The previous manual
 * writer/reader approach hung silently on production behind IAP proxies.
 */
async function _maybeDecompressGzip(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const bytes = new Uint8Array(buf);
  if (bytes.length < 2 || bytes[0] !== 0x1f || bytes[1] !== 0x8b) {
    return buf;
  }
  try {
    const ds = new DecompressionStream('gzip');
    const decompressedStream = new Blob([bytes]).stream().pipeThrough(ds);
    return await new Response(decompressedStream).arrayBuffer();
  } catch {
    const { inflate } = await import('pako');
    return inflate(bytes).buffer;
  }
}

interface TimingShardOpContext {
  queuedAt: number;
  fetchStartAt: number;
  fetchDoneAt: number;
  decodeDoneAt: number;
}

let _timingDecodeWorker: Worker | null = null;
let _timingWorkerSequence = 0;
const _timingWorkerPending = new Map<number, {
  resolve: (value: { payload: HexTimingShardPayload; decodeMs: number }) => void;
  reject: (reason?: unknown) => void;
}>();
let _timingWorkerQueueTail: Promise<void> = Promise.resolve();

function _nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function _canUseTimingDecodeWorker(): boolean {
  // Web Workers cannot be loaded through IAP-authenticated deployments because
  // the worker script request does not carry session cookies, causing a 302
  // redirect that silently fails the worker and falls back to the main-thread
  // path — but only after wasting a round trip. Disable the worker path
  // unconditionally and decode on the main thread instead, which is fast
  // enough for per-viewport shard loads (typically <50ms per shard).
  return false;
}

function _teardownTimingDecodeWorker(): void {
  if (_timingDecodeWorker) {
    _timingDecodeWorker.terminate();
    _timingDecodeWorker = null;
  }
  for (const [, pending] of _timingWorkerPending.entries()) {
    pending.reject(new Error('Timing decode worker terminated'));
  }
  _timingWorkerPending.clear();
}

function _getTimingDecodeWorker(): Worker | null {
  if (!_canUseTimingDecodeWorker()) return null;
  if (_timingDecodeWorker) return _timingDecodeWorker;
  const worker = new Worker(
    new URL('../workers/hexTimingDecode.worker.ts', import.meta.url),
    { type: 'module' },
  );
  worker.onmessage = (evt: MessageEvent<TimingDecodeResponse>) => {
    const message = evt.data;
    const pending = _timingWorkerPending.get(message.id);
    if (!pending) return;
    _timingWorkerPending.delete(message.id);
    if (!message.ok) {
      pending.reject(new Error(message.message));
      return;
    }
    pending.resolve({
      payload: message.payload as HexTimingShardPayload,
      decodeMs: message.perf.totalMs,
    });
  };
  worker.onerror = (evt) => {
    console.warn('Timing decode worker failed, disabling worker path:', evt.message);
    _teardownTimingDecodeWorker();
  };
  _timingDecodeWorker = worker;
  return worker;
}

function _decodeTimingPayloadMain(
  rawBuf: ArrayBuffer,
  version: number,
): Promise<HexTimingShardPayload> {
  return _maybeDecompressGzip(rawBuf).then((buf) => {
    const payload = decodeMsgpack(new Uint8Array(buf)) as HexTimingShardPayload;
    if (version >= 2) _deltaDecodePayload(payload, version);
    return payload;
  });
}

function _decodeTimingPayloadWorker(
  shardKey: string,
  format: string,
  version: number,
  rawBuf: ArrayBuffer,
): Promise<{ payload: HexTimingShardPayload; decodeMs: number }> {
  const worker = _getTimingDecodeWorker();
  if (!worker) return Promise.reject(new Error('Timing decode worker unavailable'));

  const runJob = (): Promise<{ payload: HexTimingShardPayload; decodeMs: number }> => {
    return new Promise((resolve, reject) => {
      const id = ++_timingWorkerSequence;
      _timingWorkerPending.set(id, { resolve, reject });
      const msg: TimingDecodeRequest = {
        id,
        shardKey,
        format,
        version,
        buffer: rawBuf,
      };
      worker.postMessage(msg);
    });
  };

  const job = _timingWorkerQueueTail.then(runJob, runJob);
  _timingWorkerQueueTail = job.then(
    () => undefined,
    () => undefined,
  );
  return job;
}

async function _fetchTimingShardPayload(
  shardKey: string,
  url: string,
  format: string,
  version: number,
  context: TimingShardOpContext,
): Promise<HexTimingShardPayload> {
  context.fetchStartAt = _nowMs();
  recordSample('queue_to_fetch', context.fetchStartAt - context.queuedAt, shardKey);

  if (format === 'msgpack') {
    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await authFetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`Fetch failed (${res.status}) for ${url}`);
      const rawBuf = await res.arrayBuffer();
      context.fetchDoneAt = _nowMs();
      recordSample('fetch_complete', context.fetchDoneAt - context.fetchStartAt, shardKey);

      if (_canUseTimingDecodeWorker()) {
        try {
          const decoded = await _decodeTimingPayloadWorker(
            shardKey,
            format,
            version,
            rawBuf,
          );
          context.decodeDoneAt = _nowMs();
          recordSample('worker_decode_complete', decoded.decodeMs, shardKey);
          return decoded.payload;
        } catch (workerErr) {
          console.warn(
            `Worker decode failed for timing shard '${shardKey}', falling back to main thread:`,
            workerErr,
          );
          _teardownTimingDecodeWorker();
        }
      }

      const decodeStartAt = _nowMs();
      const payload = await _decodeTimingPayloadMain(rawBuf, version);
      context.decodeDoneAt = _nowMs();
      recordSample('worker_decode_complete', context.decodeDoneAt - decodeStartAt, shardKey);
      return payload;
    } finally {
      clearTimeout(timeoutHandle);
    }
  }

  const payload = await _fetchJsonWithRetry<HexTimingShardPayload>(
    url,
    { attempts: 2, timeoutMs: 10000, initialBackoffMs: 200 },
  );
  context.fetchDoneAt = _nowMs();
  context.decodeDoneAt = context.fetchDoneAt;
  recordSample('fetch_complete', context.fetchDoneAt - context.fetchStartAt, shardKey);
  recordSample('worker_decode_complete', 0, shardKey);
  if (version >= 2) _deltaDecodePayload(payload, version);
  return payload;
}

/** Coalesce multiple shard merges into a single triggerRef + revision bump.
 *
 * When many shards resolve within a short window (e.g. 6 shards after a
 * zoom-out), this avoids 6 separate re-renders. A 50ms setTimeout groups
 * all completions into one Vue watcher fire → one OL render.
 */
let _mergeNotifyTimer: ReturnType<typeof setTimeout> | null = null;
function _scheduleMergeNotify() {
  if (_mergeNotifyTimer) return;
  _mergeNotifyTimer = setTimeout(() => {
    _mergeNotifyTimer = null;
    triggerRef(hexTimingData);
    hexTimingDataRevision.value++;
  }, 50);
}

function _mergeTimingArrays(existing: number[], incoming: number[], version: number): number[] {
  if (version >= 3) {
    const byTimestamp = new Map<number, number>();
    for (const values of [existing, incoming]) {
      for (let i = 0; i + 1 < values.length; i += 2) {
        byTimestamp.set(values[i], values[i + 1]);
      }
    }
    return [...byTimestamp.entries()]
      .sort((a, b) => a[0] - b[0])
      .flatMap(([timestamp, cloud]) => [timestamp, cloud]);
  }
  return [...new Set([...existing, ...incoming])].sort((a, b) => a - b);
}

function _mergeTimingPayload(payload: HexTimingShardPayload, version: number): void {
  if (!hexTimingData.value) hexTimingData.value = {};
  for (const [cellId, incomingCell] of Object.entries(payload)) {
    const targetCell = hexTimingData.value[cellId] ?? {};
    for (const [satellite, incomingTimes] of Object.entries(incomingCell)) {
      const existingTimes = targetCell[satellite];
      targetCell[satellite] = existingTimes
        ? _mergeTimingArrays(existingTimes, incomingTimes, version)
        : incomingTimes;
    }
    hexTimingData.value[cellId] = targetCell;
  }
}

interface TimingShardJob {
  source: HexShardSource;
  shardKey: string;
}

async function _loadTimingShard(source: HexShardSource, shardKey: string): Promise<boolean> {
  const loadKey = _sourceShardKey(source.id, shardKey);
  if (_loadedTimingShardKeys.has(loadKey)) return false;
  const failedAt = _failedTimingShardAt.get(loadKey);
  if (failedAt && Date.now() - failedAt < TIMING_SHARD_RETRY_COOLDOWN_MS) return false;
  const inFlight = _timingShardInFlight.get(loadKey);
  if (inFlight) return inFlight;

  const task = (async (): Promise<boolean> => {
    const index = source.index;
    const shardUrl = index.shards[shardKey];
    if (!shardUrl) return false;
    const opContext: TimingShardOpContext = {
      queuedAt: _nowMs(),
      fetchStartAt: 0,
      fetchDoneAt: 0,
      decodeDoneAt: 0,
    };

    const payload = await _fetchTimingShardPayload(
      loadKey,
      shardUrl,
      index.format ?? 'json',
      index.version ?? 1,
      opContext,
    );

    _mergeTimingPayload(payload, index.version ?? 1);
    const mergeDoneAt = _nowMs();
    hexTimingLastMergeAt.value = mergeDoneAt;
    recordSample('merge_complete', mergeDoneAt - opContext.decodeDoneAt, loadKey);
    _loadedTimingShardKeys.add(loadKey);
    _failedTimingShardAt.delete(loadKey);
    _scheduleMergeNotify();
    return true;
  })()
    .finally(() => {
      _timingShardInFlight.delete(loadKey);
    });

  _timingShardInFlight.set(loadKey, task);
  return task;
}

async function _ensureTimingShardJobs(
  jobs: TimingShardJob[],
  maxConcurrency: number,
): Promise<boolean> {
  if (jobs.length === 0) return false;
  const outcomes: boolean[] = [];
  for (let i = 0; i < jobs.length; i += maxConcurrency) {
    const batch = jobs.slice(i, i + maxConcurrency);
    const batchResults = await Promise.all(
      batch.map(async (job) => {
        const loadKey = _sourceShardKey(job.source.id, job.shardKey);
        try {
          return await _loadTimingShard(job.source, job.shardKey);
        } catch (err) {
          _failedTimingShardAt.set(loadKey, Date.now());
          if (!_isAbortError(err)) {
            console.warn(`Failed loading timing shard '${loadKey}':`, err);
          }
          return false;
        }
      }),
    );
    outcomes.push(...batchResults);
  }
  return outcomes.some(Boolean);
}

function _collectTimingJobsForCells(cellIds: string[], sources: HexShardSource[]): TimingShardJob[] {
  const jobs = new Map<string, TimingShardJob>();
  for (const source of sources) {
    const prefixLength = Math.max(1, source.index.prefixLength ?? 3);
    for (const cellId of cellIds) {
      if (!cellId) continue;
      const shardKey = cellId.slice(0, prefixLength);
      if (!source.index.shards[shardKey]) continue;
      const loadKey = _sourceShardKey(source.id, shardKey);
      if (_loadedTimingShardKeys.has(loadKey) || _timingShardInFlight.has(loadKey)) continue;
      jobs.set(loadKey, { source, shardKey });
    }
  }
  return [...jobs.values()];
}

async function ensureTimingShardsForCells(cellIds: string[]): Promise<boolean> {
  await loadHexTimingIndex();
  if (!hexTimingIndex.value) return false;
  const sources = await _getActiveHexShardSources(
    hexTimingIndex.value,
    _extendedTimingIndexes,
    _extendedTimingIndexInFlight,
    'hex timing',
  );
  const jobs = _collectTimingJobsForCells(cellIds, sources);
  return _ensureTimingShardJobs(jobs, MAX_TIMING_SHARD_CONCURRENCY);
}

async function prefetchTimingShards(shardKeys: string[]): Promise<boolean> {
  await loadHexTimingIndex();
  if (!hexTimingIndex.value) return false;
  const sources = await _getActiveHexShardSources(
    hexTimingIndex.value,
    _extendedTimingIndexes,
    _extendedTimingIndexInFlight,
    'hex timing',
  );
  const jobs: TimingShardJob[] = [];
  for (const source of sources) {
    for (const shardKey of shardKeys) {
      if (!shardKey || !source.index.shards[shardKey]) continue;
      const loadKey = _sourceShardKey(source.id, shardKey);
      if (_loadedTimingShardKeys.has(loadKey) || _timingShardInFlight.has(loadKey)) continue;
      jobs.push({ source, shardKey });
      if (jobs.length >= MAX_TIMING_PREFETCH_KEYS) break;
    }
    if (jobs.length >= MAX_TIMING_PREFETCH_KEYS) break;
  }
  return _ensureTimingShardJobs(jobs, MAX_TIMING_PREFETCH_CONCURRENCY);
}

function hasTimingShardLoadsInFlight(): boolean {
  return _timingShardInFlight.size > 0;
}

/* -------- Pass detail shards (selected-cell pass metadata, on demand) -------- */

const hexPassData = ref<Record<string, HexPassEntry[]> | null>(null);
const hexPassIndex = ref<HexPassShardIndex | null>(null);
let _hexPassIndexLoaded = false;
const _loadedPassShardKeys = new Set<string>();
const _passShardInFlight = new Map<string, Promise<boolean>>();
const _failedPassShardAt = new Map<string, number>();
const _extendedPassIndexes = new Map<string, HexPassShardIndex>();
const _extendedPassIndexInFlight = new Map<string, Promise<HexPassShardIndex | null>>();
const PASS_SHARD_RETRY_COOLDOWN_MS = 10000;
const MAX_PASS_SHARD_CONCURRENCY = 4;
let _passMergeNotifyTimer: ReturnType<typeof setTimeout> | null = null;

function _schedulePassMergeNotify() {
  if (_passMergeNotifyTimer) return;
  _passMergeNotifyTimer = setTimeout(() => {
    _passMergeNotifyTimer = null;
    triggerRef(hexPassData);
  }, 50);
}

async function loadHexPassIndex() {
  if (_hexPassIndexLoaded) return;
  _hexPassIndexLoaded = true;
  try {
    hexPassIndex.value = await _fetchJsonWithRetry<HexPassShardIndex>(
      '/data/satellite_hex_pass_index.json',
      { attempts: 3, timeoutMs: 7000, initialBackoffMs: 250 },
    );
    if (!hexPassData.value) {
      hexPassData.value = {};
    }
  } catch (err) {
    _hexPassIndexLoaded = false;
    console.warn('Failed to load hex pass-detail index (will retry on next trigger):', err);
  }
}

function hasPassDataForCell(cellId: string): boolean {
  return _hasCellDataForActiveSources(
    cellId,
    hexPassIndex.value,
    _extendedPassIndexes,
    _loadedPassShardKeys,
  );
}

function _mergePassPayload(payload: HexPassShardPayload): void {
  if (!hexPassData.value) hexPassData.value = {};
  for (const [cellId, incoming] of Object.entries(payload)) {
    const existing = hexPassData.value[cellId] ?? [];
    const byId = new Map<string, HexPassEntry>();
    for (const entry of [...existing, ...incoming]) {
      const key = `${entry.satellite}|${entry.data_source ?? ''}|${entry.start_time}`;
      byId.set(key, entry);
    }
    hexPassData.value[cellId] = [...byId.values()].sort(
      (a, b) => parseUtcEpochMs(a.start_time) - parseUtcEpochMs(b.start_time),
    );
  }
}

interface PassShardJob {
  source: HexShardSource;
  shardKey: string;
}

async function _loadPassShard(source: HexShardSource, shardKey: string): Promise<boolean> {
  const loadKey = _sourceShardKey(source.id, shardKey);
  if (_loadedPassShardKeys.has(loadKey)) return false;
  const failedAt = _failedPassShardAt.get(loadKey);
  if (failedAt && Date.now() - failedAt < PASS_SHARD_RETRY_COOLDOWN_MS) return false;
  const inFlight = _passShardInFlight.get(loadKey);
  if (inFlight) return inFlight;

  const task = (async (): Promise<boolean> => {
    const index = source.index;
    const shardUrl = index.shards[shardKey];
    if (!shardUrl) return false;

    const payload = await _fetchJsonWithRetry<HexPassShardPayload>(
      shardUrl,
      { attempts: 2, timeoutMs: 10000, initialBackoffMs: 200 },
    );

    _mergePassPayload(payload);
    _loadedPassShardKeys.add(loadKey);
    _failedPassShardAt.delete(loadKey);
    _schedulePassMergeNotify();
    return true;
  })().finally(() => {
    _passShardInFlight.delete(loadKey);
  });

  _passShardInFlight.set(loadKey, task);
  return task;
}

function _collectPassJobsForCells(cellIds: string[], sources: HexShardSource[]): PassShardJob[] {
  const jobs = new Map<string, PassShardJob>();
  for (const source of sources) {
    const prefixLength = Math.max(1, source.index.prefixLength ?? 3);
    for (const cellId of cellIds) {
      if (!cellId) continue;
      const shardKey = cellId.slice(0, prefixLength);
      if (!source.index.shards[shardKey]) continue;
      const loadKey = _sourceShardKey(source.id, shardKey);
      if (_loadedPassShardKeys.has(loadKey) || _passShardInFlight.has(loadKey)) continue;
      jobs.set(loadKey, { source, shardKey });
    }
  }
  return [...jobs.values()];
}

async function ensurePassShardsForCells(cellIds: string[]): Promise<boolean> {
  await loadHexPassIndex();
  if (!hexPassIndex.value) return false;
  const sources = await _getActiveHexShardSources(
    hexPassIndex.value,
    _extendedPassIndexes,
    _extendedPassIndexInFlight,
    'hex pass-detail',
  );
  const jobs = _collectPassJobsForCells(cellIds, sources);
  if (jobs.length === 0) return false;

  const outcomes: boolean[] = [];
  for (let i = 0; i < jobs.length; i += MAX_PASS_SHARD_CONCURRENCY) {
    const batch = jobs.slice(i, i + MAX_PASS_SHARD_CONCURRENCY);
    const batchResults = await Promise.all(
      batch.map(async (job) => {
        const loadKey = _sourceShardKey(job.source.id, job.shardKey);
        try {
          return await _loadPassShard(job.source, job.shardKey);
        } catch (err) {
          _failedPassShardAt.set(loadKey, Date.now());
          if (!_isAbortError(err)) {
            console.warn(`Failed loading pass-detail shard '${loadKey}':`, err);
          }
          return false;
        }
      }),
    );
    outcomes.push(...batchResults);
  }
  return outcomes.some(Boolean);
}

/** Pass list from timing shards + metadata catalog */
const PASS_DURATION_MS = 5 * 60 * 1000; // 5 min default segment duration

const hexTimingSelectedPassesAll = computed<VisiblePass[]>(() => {
  if (selectedHexCells.value.size === 0) return [];

  const seen = new Set<string>();
  const result: VisiblePass[] = [];
  const missingTimingCells: string[] = [];

  for (const cellId of selectedHexCells.value) {
    const cellCenter = hexCellCentroids.get(cellId);
    const detailedCell = hexPassData.value?.[cellId];

    if (Array.isArray(detailedCell)) {
      for (const entry of detailedCell) {
        const satName = String(entry?.satellite ?? '').trim();
        const startIso = String(entry?.start_time ?? '').trim();
        const dataSource = typeof entry?.data_source === 'string' ? entry.data_source : undefined;
        if (!satName || !startIso || !matchesGridPassFilters(satName, dataSource)) continue;

        const pass = buildGridVisiblePass({
          satellite: satName,
          startIso,
          endIso: entry.end_time,
          dataSource,
          direction: normalizePassDirection(entry.d),
          cloudCoverPct: entry.cloud_cover_pct,
          cellCenter,
        });
        if (!pass || seen.has(pass.id)) continue;
        seen.add(pass.id);
        result.push(pass);
      }
      continue;
    }

    missingTimingCells.push(cellId);
  }

  if (hexTimingData.value) {
    for (const cellId of missingTimingCells) {
      const timingCell = hexTimingData.value[cellId];
      if (!timingCell) continue;
      const cellCenter = hexCellCentroids.get(cellId);

      for (const [satKey, startTimes] of Object.entries(timingCell)) {
        const [satName, dataSource] = satKey.split('|');
        if (!matchesGridPassFilters(satName, dataSource)) continue;

        const step = hexTimingIndex.value?.version === 3 ? 2 : 1;
        for (let i = 0; i < startTimes.length; i += step) {
          const startSec = startTimes[i];
          const startIso = new Date(startSec * 1000).toISOString();
          const pass = buildGridVisiblePass({
            satellite: satName,
            startIso,
            dataSource,
            cloudCoverPct: step === 2 ? startTimes[i + 1] : undefined,
            cellCenter,
          });
          if (!pass || seen.has(pass.id)) continue;
          seen.add(pass.id);
          result.push(pass);
        }
      }
    }
  }

  return result
    .filter(passWithinCloudThreshold)
    .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));
});

const hexTimingSelectedPasses = computed<VisiblePass[]>(() => {
  const refTimeSec = Math.floor(timeRange.value[0] / 1000);
  const endTimeSec = Math.floor(timeRange.value[1] / 1000);
  return hexTimingSelectedPassesAll.value.filter((p) => {
    const startSec = Math.floor(parseUtcEpochMs(p.start_time) / 1000);
    return startSec >= refTimeSec && startSec <= endTimeSec;
  });
});

/* ───────────── Hex grid coverage (static artifacts, not live) ───────────── */

/**
 * What the H3 artifacts actually cover, as opposed to what the time range allows.
 *
 * The swath and AOI views are live queries across the predictor's whole served
 * window (roughly +-15 days), but the grid is still precomputed by the daily
 * pipeline and covers far less: from the build forward only, all satellites for
 * `defaultForecastHours`, then the OroraTech constellation alone for a few more
 * days. Drag the range outside that and cells simply go blank, which reads as
 * "no overpasses here" rather than "not computed".
 *
 * The boundaries are read from the shard index rather than assumed, since
 * `extendedShards` encodes them exactly: the first extended window begins where
 * all-satellite coverage ends, and the last one ends where coverage stops.
 */
export interface GridCoverageWindow {
  /** Nothing before this was precomputed. */
  startMs: number;
  /** Past this, only the OroraTech constellation is covered. */
  allSatellitesEndMs: number;
  /** Past this, nothing is covered. */
  endMs: number;
}

const gridCoverage = computed<GridCoverageWindow | null>(() => {
  const meta = metadata.value;
  if (!meta) return null;
  const startMs = parseUtcEpochMs(meta.lastUpdated);
  if (!Number.isFinite(startMs)) return null;

  const defaultHours = Number.isFinite(meta.defaultForecastHours)
    ? Math.max(1, Number(meta.defaultForecastHours))
    : FALLBACK_FORECAST_HOURS;

  const shards = hexTimingIndex.value?.extendedShards ?? [];
  const starts = shards.map((s) => parseUtcEpochMs(s.startTime)).filter((ms) => Number.isFinite(ms));
  const ends = shards.map((s) => parseUtcEpochMs(s.endTime)).filter((ms) => Number.isFinite(ms));

  // Without extended shards the OT extension is simply absent, so both horizons
  // land on the all-satellite one.
  const allSatellitesEndMs = starts.length ? Math.min(...starts) : startMs + defaultHours * HOUR_MS;
  const endMs = ends.length ? Math.max(...ends) : allSatellitesEndMs;
  return { startMs, allSatellitesEndMs, endMs };
});

/** Which parts of the selected range the grid cannot answer for. */
const gridCoverageGaps = computed(() => {
  const coverage = gridCoverage.value;
  const [rangeStart, rangeEnd] = timeRange.value;
  if (!coverage || !Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd)) {
    return { past: false, otOnly: false, beyondAll: false, any: false };
  }
  const past = rangeStart < coverage.startMs;
  const otOnly = rangeEnd > coverage.allSatellitesEndMs;
  const beyondAll = rangeEnd > coverage.endMs;
  return { past, otOnly, beyondAll, any: past || otOnly || beyondAll };
});

/** The currently selected pass object (derived) */
const selectedPass = computed<VisiblePass | null>(() => {
  if (!selectedPassId.value) return null;
  return visiblePasses.value.find((p) => p.id === selectedPassId.value) ?? null;
});

/* ──────────────────────── Actions ──────────────────────────── */

let _loaded = false;
let _loadPromise: Promise<void> | null = null;

/**
 * Fetch the served roster. Never throws — a missing roster degrades the app to the
 * static catalogue, it does not stop it starting.
 */
async function loadSatelliteRoster(): Promise<SatelliteRoster | null> {
  try {
    const document = await _fetchJsonWithRetry<RawRosterDocument>(
      '/api/satellites',
      { attempts: 2, timeoutMs: 7000, initialBackoffMs: 250 },
    );
    satelliteRoster.value = parseRoster(document);
  } catch (err) {
    console.warn('Satellite roster unavailable; falling back to the static catalogue:', err);
    satelliteRoster.value = null;
  }
  return satelliteRoster.value;
}

/**
 * Constellation members that exist but are not exposed in the product yet.
 *
 * The prediction service serves these and the pipeline's metadata lists them, but
 * MapFilterPanel has no checkbox for them (their entries are commented out there),
 * so a user could not switch them off. Rather than render satellites nobody can
 * control, they are excluded from every query this app makes — live swaths, AOI
 * coverage and scheduled footprints alike.
 *
 * To expose one, remove it here and add its checkbox in MapFilterPanel; both are
 * needed. Satellites with limited access belong in INTERNAL_ONLY_SATELLITE_NAMES.
 *
 * Note this does not affect scripts/fetch_swaths.py, which still includes them when
 * building the static H3 artifacts.
 */
const NON_QUERYABLE_SATELLITE_NAMES = new Set([
  'FOREST-3',
  'FOREST-12',
  'FOREST-13',
  'FOREST-14',
  'FOREST-15',
  'FOREST-16',
  'FOREST-17',
  'FOREST-18',
  'FOREST-19',
  'GENA-OT',
]);

const INTERNAL_ONLY_SATELLITE_NAMES = new Set(['FOREST-2']);
const { isInternalUser } = useAuth();

/** False for satellites hidden from the product or unavailable to the current user. */
function isQueryableSatellite(name: string): boolean {
  const normalizedName = String(name ?? '').trim().toUpperCase();
  if (NON_QUERYABLE_SATELLITE_NAMES.has(normalizedName)) return false;
  return isInternalUser.value || !INTERNAL_ONLY_SATELLITE_NAMES.has(normalizedName);
}

/**
 * Display names → NORAD IDs the prediction service will actually answer for.
 *
 * Resolution goes through the live roster first and the static SAT_NORAD_MAP only
 * as a fallback, and IDs the service does not serve are dropped rather than sent:
 * an unserved ID fails the whole upstream query instead of returning a shorter
 * result set, so one stale entry would empty the map.
 */
function resolveServedNoradIds(names: readonly string[]): number[] {
  const roster = satelliteRoster.value;
  const ids = new Set<number>();
  for (const rawName of names) {
    const name = String(rawName ?? '').trim();
    if (!name || !isQueryableSatellite(name)) continue;
    const id = roster?.byName.get(name.toUpperCase()) ?? SAT_NORAD_MAP[name];
    if (id == null) continue;
    if (roster && !roster.byNorad.has(id)) continue;
    ids.add(id);
  }
  return [...ids];
}

/** Every satellite the service serves, for queries that deliberately ignore the UI filter. */
function servedCatalogNoradIds(): number[] {
  const roster = satelliteRoster.value;
  if (roster) {
    return [...roster.byNorad.values()]
      .filter((entry) => isQueryableSatellite(entry.name))
      .map((entry) => entry.noradId);
  }
  return [...new Set(
    Object.entries(SAT_NORAD_MAP)
      .filter(([name]) => isQueryableSatellite(name))
      .map(([, id]) => id),
  )];
}

/** Total swath width in km for a NORAD ID, from the service's calibrated extents. */
function swathWidthKmForNorad(noradId: number): number | null {
  const entry = satelliteRoster.value?.byNorad.get(noradId);
  if (!entry) return null;
  const total = entry.swathStarboardKm + entry.swathPortKm;
  return total > 0 ? total : null;
}

function loadMetadata(): Promise<void> {
  if (_loadPromise) return _loadPromise;
  if (_loaded) return Promise.resolve();
  _loaded = true;
  loading.value = true;

  _loadPromise = (async () => {
    try {
      // The roster is fetched alongside, not before: it is an upgrade, not a
      // prerequisite. If the prediction gateway is unreachable the app still comes
      // up on the static metadata, one pipeline run behind.
      const [data, roster] = await Promise.all([
        _fetchJsonWithRetry<SatelliteMetadata>(
          '/data/satellite_paths_metadata.json',
          { attempts: 3, timeoutMs: 7000, initialBackoffMs: 250 },
        ),
        loadSatelliteRoster(),
      ]);
      metadata.value = data;

      // Reconcile persisted selections to current metadata and naming.  Deliberately
      // still keyed on the static list: MapFilterPanel renders its own hardcoded
      // sensor groups, so a satellite selected here but absent there would appear on
      // the map with no checkbox to turn it off.
      const reconciledSatellites = reconcileSelectedSatellites(data.satellites);
      if (!sameSelection(selectedSatelliteNames.value, reconciledSatellites)) {
        selectedSatelliteNames.value = reconciledSatellites;
      }

      const windows = getForecastWindows(data, roster);
      metaTimeRange.value = windows.available;
      standardForecastEndMs.value = windows.standardEnd;
      timeRange.value = windows.initial;
      // Eagerly preload timing shard index so grid view is ready instantly
      loadHexTimingIndex();
      loadHexPassIndex();
    } catch (err) {
      console.error('Failed to load satellite metadata:', err);
      _loaded = false; // allow retry on next call
    } finally {
      loading.value = false;
      _loadPromise = null;
    }
  })();

  return _loadPromise;
}

/**
 * Returns `true` when the feature properties satisfy the active filters.
 * Used by the style function on the VectorTile layer to show/hide features.
 *
 * Filtering is based on:
 *  1. Time range (start_time / end_time must overlap the selected range)
 *  2. Satellite name must be in the selectedSatelliteNames list (if non-empty)
 */
function featurePassesFilter(props: Record<string, unknown>): boolean {
  // Time filter
  const startTime = parseUtcEpochMs(String(props.viewport_start_time ?? props.start_time ?? ''));
  const endTime = parseUtcEpochMs(String(props.viewport_end_time ?? props.end_time ?? ''));
  if (startTime > timeRange.value[1] || endTime < timeRange.value[0]) return false;

  // Satellite name filter
  const satName = String(props.satellite ?? '').toUpperCase();
  if (!passWithinForecastHorizon(satName, startTime, String(props.data_source ?? ''))) return false;
  const matched = selectedSatelliteNames.value.some(
    (name) => satName === name.toUpperCase(),
  );
  if (!matched) return false;

  // OroraTech data source filter
  if (isOroraTechConstellationSatellite(satName)) {
    const dataSource = String(props.data_source ?? '');
    if (otDataSourceFilter.value === 'predicted') {
      // Predicted mode: exclude only explicitly scheduled features
      if (dataSource === 'forest_client') return false;
    } else {
      // Scheduled mode: include only forest_client features
      if (dataSource !== 'forest_client') return false;
    }
  }

  // Cloud cover threshold filter
  if (cloudCoverThreshold.value < 100) {
    const cloud = props.cloud_cover_pct;
    if (cloud != null) {
      const cloudPct = Number(cloud);
      if (Number.isFinite(cloudPct) && cloudPct > cloudCoverThreshold.value) return false;
    }
  }

  return true;
}

/* ──────────────────────── Composable ─────────────────────── */

export function useFilterStore() {
  return {
    metadata: readonly(metadata),
    satelliteRoster: readonly(satelliteRoster),
    loadSatelliteRoster,
    isQueryableSatellite,
    resolveServedNoradIds,
    servedCatalogNoradIds,
    swathWidthKmForNorad,
    loading: readonly(loading),
    swathLoading: readonly(swathLoading),
    setSwathLoading,

    metaTimeRange,
    timeRange,
    standardForecastEndMs: readonly(standardForecastEndMs),
    passWithinForecastHorizon,
    selectedSatelliteNames,
    ororaConstellationExpanded,
    otDataSourceFilter,
    cloudCoverThreshold,
    passWithinCloudThreshold,

    selectedPassId,
    hoveredPassId,
    selectedPassSourceIds: readonly(selectedPassSourceIds),
    hoveredPassSourceIds: readonly(hoveredPassSourceIds),
    coverageHoverActive: readonly(coverageHoverActive),
    setSelectedPass,
    setHoveredPass,
    selectedPass,

    overpassLayerVisible,
    overpassLayerOpacity,
    eumetsatTrueRgbVisible,
    eumetsatTrueRgbOpacity,
    eumetsatInfraredVisible,
    eumetsatInfraredOpacity,

    hexGridVisible,
    hexGridOpacity,
    aoiCoverageVisible,
    aoiCoverageOpacity,

    visiblePasses,
    visiblePassCount,
    swathGraphPasses,

    // Grid (hex) view
    mapViewMode,
    gridCoverage,
    gridCoverageGaps,
    selectedHexCells,
    hexTimingSelectedPassesAll,
    hexTimingSelectedPasses,
    hexCellCentroids,

    // Timing shards (viewport coloring + pass list)
    hexTimingData,
    hexTimingDataRevision,
    hexTimingIndex,
    hexTimingLastMergeAt: readonly(hexTimingLastMergeAt),
    hasTimingDataForCell,
    ensureTimingShardsForCells,
    prefetchTimingShards,
    hasTimingShardLoadsInFlight,
    loadHexTimingIndex,
    hexPassData,
    hexPassIndex,
    hasPassDataForCell,
    ensurePassShardsForCells,
    loadHexPassIndex,
    hexTimingPerf,

    loadMetadata,
    featurePassesFilter,
  };
}
